import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
// Import Shared Components
import { Table, Loader, ErrorComponent } from 'shared-components';
import {
  getFormattedMerchantInfo,
  getFormattedTransactionDate,
  getFormattedTransactionDescription,
} from 'shared-components/src/utils/cardholders';
// Import Shared Utils
import { get } from 'shared-components/src/utils/http';
import { formatCurrencySV } from 'shared-components/src/utils/formatCurrency';
// Import Global Components
import Alert from '../../../../../../components/Alert';

const LocalAmountSpan = styled.span`
  color: #3f48cc;
  margin-left: 24px;
`;

// The transactions endpoint pages its results (100 rows unless told otherwise) and
// the authorizations endpoint does not, so the two are merged and paged here. That
// only works if every settled page is read first: asking for page 1 alone silently
// dropped everything past the 100th transaction in the range.
export const TRANSACTIONS_PAGE_LIMIT = 100;
// A ceiling on how far the walk goes, so one very busy account cannot keep the
// page loading. Six months, the widest range offered, rarely comes near it.
export const TRANSACTIONS_MAX_PAGES = 20;

// The E6 path reports no page count, so a page shorter than the limit is the only
// sign of the last one.
export const fetchAllTransactions = async (accountId, startDate, endDate) => {
  const rows = [];
  for (let page = 1; page <= TRANSACTIONS_MAX_PAGES; page += 1) {
    const query = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      page,
      limit: TRANSACTIONS_PAGE_LIMIT,
    }).toString();

    // Each page decides whether there is another, so they cannot be read in parallel.
    // eslint-disable-next-line no-await-in-loop
    const { data } = await get(
      `/api/v1/cardholder_portal/cardholder/accounts/${accountId}/transactions?${query}`
    );
    rows.push(...data);

    if (data.length < TRANSACTIONS_PAGE_LIMIT) return { rows, truncated: false };
  }
  return { rows, truncated: true };
};

const TransactionsTable = ({
  accountId,
  startDate,
  endDate,
  handleCsvData,
}) => {
  const intl = useIntl();

  // Set component states
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  // Set data states
  const [transactions, setTransactions] = useState([]);
  const [authorizations, setAuthorizations] = useState([]);
  const [truncated, setTruncated] = useState(false);

  useEffect(() => {
    // A range changed mid-walk must not have its rows overwritten by the old one's.
    let live = true;

    const fetchData = async () => {
      setError(false);
      setLoading(true);

      try {
        const [
          transactionsData,
          authorizationsData,
        ] = await Promise.all([
          fetchAllTransactions(accountId, startDate, endDate),
          get(
            `/api/v1/cardholder_portal/cardholder/accounts/${accountId}/authorizations`
          ),
        ]);

        if (!live) return;

        const supplementedTransactionsData = transactionsData.rows.map(
          transaction => ({
            ...transaction,
            status: 'settled',
          })
        ).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

        const supplementedAuthData = authorizationsData.data.map(auth => ({
          ...auth,
          status: 'pending',
        })).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

        setTransactions(supplementedTransactionsData);
        setAuthorizations(supplementedAuthData);
        setTruncated(transactionsData.truncated);

        handleCsvData([
          ...supplementedAuthData,
          ...supplementedTransactionsData,
        ]);
      } catch (e) {
        if (!live) return;
        setError(true);
      }

      setLoading(false);
    };

    fetchData();
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, accountId]);

  // Define page states
  if (error) return <ErrorComponent />;
  if (loading) return <Loader />;

  const columns = [
    {
      width: 175,
      Header: () => (
        <FormattedMessage
          id="txn-date-table-header"
          description="Table Header txn date"
          defaultMessage={intl.messages['txn-date-table-header']}
        />
      ),
      accessor: 'timestamp',
      // eslint-disable-next-line react/prop-types
      Cell: ({ value }) => getFormattedTransactionDate(value, intl.locale),
    },
    {
      width: 200,
      Header: () => (
        <FormattedMessage
          id="txn-description-table-header"
          description="Table Header txn description"
          defaultMessage={intl.messages['txn-description-table-header']}
        />
      ),
      accessor: 'description',
      Cell: cell => {
        const description = getFormattedTransactionDescription(cell.original);
        return <div>{description}</div>;
      },
    },
    {
      width: 125,
      Header: () => (
        <FormattedMessage
          id="txn-status-table-header"
          description="Table Header txn status info"
          defaultMessage={intl.messages['txn-status-table-header']}
        />
      ),
      accessor: 'status',
      Cell: ({ value }) => intl.messages[`transaction-status-${value}`],
    },
    {
      width: 450,
      Header: () => (
        <FormattedMessage
          id="txn-merchant-info-table-header"
          description="Table Header txn merchant info"
          defaultMessage={intl.messages['txn-merchant-info-table-header']}
        />
      ),
      accessor: 'merchant',
      Cell: cell => {
        const { info, localAmount } = getFormattedMerchantInfo(cell.original);
        return (
          <div>
            {info}
            {localAmount && <LocalAmountSpan>({localAmount})</LocalAmountSpan>}
          </div>
        );
      },
    },
    {
      width: 125,
      Header: () => (
        <div
          style={{
            textAlign: 'right',
          }}
        >
          <FormattedMessage
            id="txn-amount-table-header"
            description="Table Header txn amount"
            defaultMessage={intl.messages['txn-amount-table-header']}
          />
        </div>
      ),
      Cell: cell => {
        // Use amount field if exists (galileo transaction),
        // if amount field is falsy we then check if auth_amount (galileo auth transaction)
        // if both are false we default to billing amount (carta transaction)
        const amount =
          cell.original.amount ||
          cell.original.auth_amount ||
          cell.original.billing_amount;

        // Get currency code or default to 'CAD'
        const currencyCode = cell.original.billing_currency || 'CAD';
        const formattedAmount = formatCurrencySV(
          amount === '' ? '0' : amount,
          currencyCode,
          true
        );

        return (
          <div
            style={{
              width: '100%',
              'text-align': 'right',
            }}
          >
            {formattedAmount}
          </div>
        );
      },
    },
  ];

  return (
    <>
      {transactions.length === 0 && authorizations.length === 0 ? (
        <Alert
          type="info"
          alert={intl.messages['home-table-alert']}
          alertFor="table"
          page="home"
        />
      ) : (
        <>
          {truncated && (
            <Alert
              type="info"
              alert={intl.messages['home-table-truncated-alert']}
              alertFor="table-truncated"
              page="home"
            />
          )}
          <Table
            data={[...authorizations, ...transactions]}
            columns={columns}
          />
        </>
      )}
    </>
  );
};

TransactionsTable.propTypes = {
  accountId: PropTypes.number.isRequired,
  startDate: PropTypes.string.isRequired,
  endDate: PropTypes.string.isRequired,
  handleCsvData: PropTypes.func.isRequired,
  accountBalance: PropTypes.shape({
    currency: PropTypes.string.isRequired,
    balance: PropTypes.number.isRequired,
  }).isRequired,
};
export default TransactionsTable;
